import { useMutation } from '@apollo/client';
import { Box, Spinner, TextArea, ToastAction, ToastType, useModalState, useToastsDispatch } from '@bechtel/gds';
import { DELETE_PROJECT_CONFIGURATION, UPDATE_PROJECT_CONFIGURATION, useAppUser } from '@bechtel/ht-clients-common';
import { ProjectConfiguration } from '@bechtel/ht-types';
import { FC, useEffect, useState } from 'react';
import { ActionContent, ConfirmDialog, ErrorBoundary } from '~/components';
import { PrimaryButtonStyled, SecondaryButtonStyled } from '~/styles';
export interface ProjectConfigurationDetailsProps {
  configurationData: ProjectConfiguration;
  onItemDelete: () => void;
}
export const ProjectConfigurationDetails: FC<ProjectConfigurationDetailsProps> = ({
  configurationData,
  onItemDelete,
}) => {
  const [configurationValue, setConfigurationValue] = useState(
    JSON.stringify(configurationData?.configurationValue) || ''
  );
  const { appUser } = useAppUser();

  const dispatchToast = useToastsDispatch();
  const { visible: showConfirmDialog, open: openConfirmDialog, close: closeConfirmCartDialog } = useModalState(false);

  const [UpdateProjectConfiguration, { loading: loadingProjectConfiguration }] = useMutation(
    UPDATE_PROJECT_CONFIGURATION,
    {
      refetchQueries: ['searchProjectConfigurations'],
    }
  );

  const updateProjectConfiguration = async (projectConfigurationValue: string) => {
    try {
      JSON.parse(projectConfigurationValue);
    } catch (error) {
      const errorMessage: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.DANGER,
          content: 'Invalid json object value. Please correct and retry!',
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(errorMessage);
      return;
    }
    // remove __typename from payload to save since server will report error if included
    const dataToSave: ProjectConfiguration = { ...configurationData, __typename: undefined };
    await UpdateProjectConfiguration({
      variables: {
        updateProjectConfigurationInput: {
          ...dataToSave,
          //configurationValue: JSON.parse(projectConfigurationValue),
          configurationValue: btoa(projectConfigurationValue), // encode to base64
          updatedBy: appUser?.bun,
          updatedDate: new Date(),
        },
      },
      onCompleted: (data) => {
        const configurationSuccessToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: 'Configuration updated successfully!',
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(configurationSuccessToast);
      },
      onError: (error) => {
        const configurationErrorToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: 'Error while  updating the configuration',
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(configurationErrorToast);
      },
    });
  };
  const [DeleteProjectConfiguration, { loading: loadingDeleteProjectConfiguration }] = useMutation(
    DELETE_PROJECT_CONFIGURATION,
    {
      refetchQueries: ['SearchProjectConfigurations'],
    }
  );

  const deleteProjectConfiguration = async () => {
    closeConfirmCartDialog();
    if (!configurationData.id) {
      const errorMessage: ToastAction = {
        type: 'ADD_TOAST',
        payload: {
          toastType: ToastType.DANGER,
          content: 'Invalid configuration data.Please refresh the screen and try again.',
          autoDismissTimeout: 5000,
        },
      };
      dispatchToast(errorMessage);
      return;
    }
    await DeleteProjectConfiguration({
      variables: {
        deleteProjectConfigurationInput: {
          id: configurationData.id,
        },
      },
      onCompleted: (data) => {
        const configurationSuccessToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: 'Configuration deleted successfully!',
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(configurationSuccessToast);
        onItemDelete();
      },
      onError: (error) => {
        const configurationErrorToast: ToastAction = {
          type: 'ADD_TOAST',
          payload: {
            toastType: ToastType.INFO,
            content: 'Error while  deleting the configuration',
            autoDismissTimeout: 5000,
          },
        };
        dispatchToast(configurationErrorToast);
      },
    });
  };

  useEffect(() => {
    setConfigurationValue(JSON.stringify(configurationData?.configurationValue));
  }, [configurationData?.configurationValue]);
  return (
    <ErrorBoundary>
      <Box
        css={{ position: 'relative', width: '100%', height: '98%' }}
        m={{ sm: '200' }}
        flexDirection={{ sm: 'column' }}
        justifyContent={{ sm: 'space-between' }}
      >
        <Box style={{ height: '200px', width: '100%' }}>
          <TextArea
            value={configurationValue}
            onChange={(event) => setConfigurationValue(event.target.value)}
          ></TextArea>
        </Box>
        <Box justifyContent={{ sm: 'flex-end' }} p={{ sm: '400' }} gap={{ sm: '400' }}>
          <PrimaryButtonStyled
            onClick={() => updateProjectConfiguration(configurationValue)}
            disabled={configurationValue.length <= 0}
          >
            <ActionContent icon="save" iconStyle="solid" label="Update" />
            {loadingProjectConfiguration && <Spinner />}
          </PrimaryButtonStyled>
          <SecondaryButtonStyled
            onClick={() => {
              openConfirmDialog();
            }}
            disabled={configurationValue.length <= 0}
          >
            <ActionContent icon="trash" label="Delete" />
            {loadingDeleteProjectConfiguration && <Spinner />}
          </SecondaryButtonStyled>
        </Box>
      </Box>
      {showConfirmDialog && (
        <ConfirmDialog
          onCancel={closeConfirmCartDialog}
          onConfirm={deleteProjectConfiguration}
          title="Confirmation Message"
          message="Are you sure you want to delete this configuration for the current project?"
          cancelText="No"
          confirmText="Yes"
        ></ConfirmDialog>
      )}
    </ErrorBoundary>
  );
};
